import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RouterProvider } from 'react-router';
import { FC, ReactElement } from 'react';
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import { useWhoamiStore } from '@/store/useWhoamiStore.ts';
import Logout from '@/pages/Authentication/Logout/Logout.tsx';
import Error404 from '@/pages/errors/Error404/Error404.tsx';
import Error403 from '@/pages/errors/Error403/Error403.tsx';
import routes from '@/constants/routes.ts';
import Login from '@/pages/Authentication/Login/Login.tsx';
import ForgotPassword from '@/pages/Authentication/ForgotPassword/ForgotPassword.tsx';
import ResetPassword from '@/pages/Authentication/ResetPassword/ResetPassword.tsx';
import SsoRegister from '@/pages/Authentication/SsoRegister/SsoRegister.tsx';
import NoOrganization from '@/pages/Authentication/NoOrganization/NoOrganization.tsx';
import AuthError from '@/pages/Authentication/AuthError/AuthError.tsx';
import EmployeeOnboard from '@/pages/Authentication/EmployeeOnboard/EmployeeOnboard.tsx';
import SpotnanaRedirection from '@/pages/SpotnanaRedirection/SpotnanaRedirection.tsx';
import ActivateAccount from '@/pages/Authentication/ActivateAccount/ActivateAccount.tsx';
import ThankYouForFeedback from '@/pages/ThankYouForFeedback/ThankYouForFeedback.tsx';
import usePersistentPreferencesStore from '@/store/usePersistentPreferencesStore.ts';
import ClimateProDashboard from '@/pages/ClimateProDashboard/ClimateProDashboard.tsx';
import EmissionTarget from '@/pages/ClimateProDashboard/EmissionTarget/EmissionTarget.tsx';
import SecuritySettings from '@/pages/Authentication/SecuritySettings/SecuritySettings.tsx';
import Allocation from '@/pages/ClimateProDashboard/Allocation/Allocation.tsx';
import ExcessAlerts from '@/pages/ClimateProDashboard/ExcessAlerts/ExcessAlerts.tsx';
import { Roles } from '@/api/user.ts';
import FactorFramework from '@/pages/ClimateProDashboard/FactorFramework/FactorFramework.tsx';
import Metrics from '@/pages/ClimateProDashboard/Metrics/Metrics.tsx';
import AiPredictiveAnalytics from '@/pages/ClimateProDashboard/AIPredictiveAnalytics/AIPredictiveAnalytics.tsx';
import Certificates from '@/pages/ClimateProDashboard/Certificates/Certificates.tsx';
import ReportDownload from '@/pages/ClimateProDashboard/ReportDownload/ReportDownload.tsx';
import MyClimate from '@/pages/MyClimate/MyClimate.tsx';
import TripEmissionLog from '@/pages/MyClimate/TripEmissionLog/TripEmissionLog.tsx';
import CO2Dashboard from '@/pages/ClimateProDashboard/CO2Dashboard/CO2Dashboard.tsx';
import OrganizationLogin from '@/pages/OrganizationLogin/OrganizationLogin.tsx';
import Error50x from '@/pages/errors/Error50x/Error50x.tsx';
import PastTrips from '@/pages/ClimateProDashboard/DataCompliance/PastTrips/PastTrips.tsx';
import Settings from '@/pages/ClimateProDashboard/DataCompliance/Settings/Settings.tsx';
import EmissionCalculator from '@/pages/EmissionCalculator/EmissionCalculator.tsx';
import AlkSsoRedirect from '@/pages/Authentication/AlkSsoRedirect/AlkSsoRedirect.tsx';
import RootRedirect from '@/core/RootRedirect.tsx';
import { redirectToPendingAuthTarget } from '@/utils/authRedirect.ts';

type RouteRole = Roles | App.Enums.SpotnanaRole;

interface Route {
	path: string;
	element?: ReactElement;
	roles?: RouteRole[];
	children?: Route[];
}

/**
 * Routes that will be shown to both guest and authenticated users
 */
const commonRoutes: Route[] = [
	{ path: '/', element: <RootRedirect /> },
	{ path: '/user', element: <Navigate to={routes.myClimate} /> }, // TODO - remove this after release
	{ path: routes.logout, element: <Logout /> },
	{ path: '*', element: <Error404 /> },
	{ path: '/error', element: <Error50x /> },
	{ path: '/not-authorized', element: <Error403 /> },
	{ path: routes.thankYouForFeedback, element: <ThankYouForFeedback /> },
	{ path: routes.emissionCalculator, element: <EmissionCalculator /> },
];

/**
 * Routes that will be shown to authenticated users only
 */

const climateProDashboardRoutes = [
	{ path: 'setting', element: <EmissionTarget /> },
	{ path: 'allocation', element: <Allocation /> },
	{ path: 'excess-alerts', element: <ExcessAlerts /> },
	{ path: 'factor-framework', element: <FactorFramework /> },
	{ path: 'metrics', element: <Metrics /> },
	{ path: 'dashboard', element: <CO2Dashboard /> },
	{ path: 'analytics', element: <AiPredictiveAnalytics /> },
	{ path: 'certificates', element: <Certificates /> },
	{ path: 'report', element: <ReportDownload /> },
	{ path: 'co2-reporting-quality', element: <PastTrips /> },
	{ path: 'co2-reporting-quality-settings', element: <Settings /> },
];

const authenticatedRoutes: Route[] = [
	{ path: routes.myClimate, element: <MyClimate /> },
	{
		path: routes.employeeCreate,
		element: <EmployeeOnboard mode="admin" />,
		roles: [Roles.CompanyAdmin, Roles.TmcAdmin, Roles.TmcAgent],
	},
	{
		path: routes.climatePro,
		element: <ClimateProDashboard />,
		children: [
			{ path: '', element: <Navigate to={routes.dataCompliancePastTrips} replace /> },
			...climateProDashboardRoutes,
		],
		roles: [Roles.CompanyAdmin, Roles.TmcAdmin, Roles.TmcAgent],
	},
	{ path: routes.spotnanaRedirection, element: <SpotnanaRedirection /> },
	{ path: routes.securitySettings, element: <SecuritySettings /> },
	{ path: routes.tripEmissionLog, element: <TripEmissionLog /> },
	{ path: '/my-climate/trip-emission-log-data-compliance', element: <TripEmissionLog /> },
];

/**
 * Routes that will be shown to both guests only
 */
const guestRoutes: Route[] = [
	{ path: routes.login, element: <Login /> },
	{ path: routes.organizationLogin, element: <OrganizationLogin /> },
	{ path: routes.forgotPassword, element: <ForgotPassword /> },
	{ path: routes.resetPassword, element: <ResetPassword /> },
	{ path: routes.ssoRegister, element: <SsoRegister /> },
	{ path: routes.noOrganization, element: <NoOrganization /> },
	{ path: routes.authError, element: <AuthError /> },
	{ path: routes.employeeOnboard, element: <EmployeeOnboard /> },
	{ path: routes.activateAccount, element: <ActivateAccount /> },
	{ path: routes.alkSsoRedirection, element: <AlkSsoRedirect /> },
];

const LoginRedirection: FC<{ returnPath: string }> = ({ returnPath }) => {
	const { setReturnPath } = usePersistentPreferencesStore();

	setReturnPath(returnPath);

	return <Navigate to={routes.login} />;
};

// Get all routes based on loggedIn state
const createRoutes = (loggedIn: boolean, userRole: RouteRole | null | undefined): Route[] => {
	return [
		...commonRoutes,
		...authenticatedRoutes.map(route => {
			if (!loggedIn) {
				return { ...route, element: <LoginRedirection returnPath={route.path} /> };
			}

			if (
				route.roles &&
				((userRole && !route.roles.includes(userRole)) || userRole === null || userRole === undefined)
			) {
				// User doesn't have permission for this route, redirect to dashboard
				return {
					...route,
					element: <Error404 />,
				};
			}

			return route;
		}),
		...guestRoutes.map(route => {
			if (loggedIn) {
				// If logged in and trying to access forgot password -> go to security settings
				if (route.path === routes.forgotPassword) {
					return { ...route, element: <Navigate to={routes.securitySettings} replace /> };
				}
				// For all other guest pages, go to my climate
				return { ...route, element: <Navigate to={routes.myClimate} replace /> };
			}

			// If not logged in, show the guest page as normal
			return route;
		}),
	];
};

const Router = () => {
	const loggedIn = useWhoamiStore(state => state.loggedIn);
	const hasResolvedWhoami = useWhoamiStore(state => state.hasResolvedWhoami);
	const redirectUrl = useWhoamiStore(state => state.redirectUrl);
	const userRole = useWhoamiStore(state => state.user?.spotnanaRole);

	if (loggedIn === true && hasResolvedWhoami && redirectToPendingAuthTarget(redirectUrl, { replace: true })) {
		return <Spinner />;
	}

	// Wait for the initial whoami, before getting any persisted return path
	if (loggedIn === undefined || !hasResolvedWhoami) {
		return <Spinner />;
	}

	// Get all routes, conditionally based on the loggedIn state
	const routes = createRoutes(loggedIn, userRole || null);

	// Create router
	const router = createBrowserRouter(routes);

	return <RouterProvider router={router} />;
};

export default Router;
